import { ArrowLeft, BarChart3, CalendarClock, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { getLandingUrl } from '../lib/urls';

interface AuthShellProps {
  title: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  compact?: boolean;
}

export const authInputClass =
  'control w-full bg-background/60 px-3.5 text-foreground placeholder:text-muted-foreground';
export const authLabelClass = 'mb-2 block text-sm font-semibold text-foreground';
export const authErrorClass = 'mt-2 text-sm font-medium text-danger';

export function AuthShell({ title, subtitle, children, footer, compact = false }: AuthShellProps) {
  const { t } = useTranslation();

  return (
    <main className="relative min-h-screen overflow-hidden bg-background text-foreground lg:grid lg:grid-cols-[minmax(0,1.05fr)_minmax(30rem,0.95fr)]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_12%,hsl(var(--primary)/0.22),transparent_30rem),radial-gradient(circle_at_88%_88%,hsl(var(--secondary)/0.11),transparent_28rem)]" />

      <section className="relative hidden min-h-screen border-r border-border/70 p-10 lg:flex lg:flex-col xl:p-14">
        <a href={getLandingUrl()} className="inline-flex w-fit items-center rounded-xl focus-visible:outline-none">
          <img src="/logo.png" alt="SUSCRI.net" className="h-11 w-auto" />
        </a>
        <div className="my-auto max-w-xl py-16">
          <span className="status-badge border-secondary/20 bg-secondary/10 text-secondary">
            <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
            {t('Gestión inteligente de suscripciones')}
          </span>
          <h1 className="mt-7 text-4xl font-bold leading-[1.08] tracking-[-0.045em] xl:text-5xl">
            {t('Todo tu gasto recurrente,')} <span className="text-gradient">{t('bajo control.')}</span>
          </h1>
          <p className="mt-5 max-w-lg text-base leading-7 text-muted-foreground xl:text-lg">
            {t('Centraliza suscripciones, anticipa renovaciones y entiende en qué se va tu dinero desde un único panel.')}
          </p>
          <div className="mt-9 grid gap-3 sm:grid-cols-3">
            <Feature icon={BarChart3} label={t('Gasto claro')} />
            <Feature icon={CalendarClock} label={t('Alertas a tiempo')} />
            <Feature icon={ShieldCheck} label={t('Datos bajo control')} />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} SUSCRI.net</p>
      </section>

      <section className="relative flex min-h-screen items-center justify-center px-4 py-8 sm:px-8 lg:px-12">
        <div className={`w-full ${compact ? 'max-w-md' : 'max-w-lg'}`}>
          <div className="mb-7 flex items-center justify-between lg:hidden">
            <img src="/logo.png" alt="SUSCRI.net" className="h-10 w-auto" />
            <a href={getLandingUrl()} className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card/70 text-muted-foreground hover:bg-accent hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">{t('Volver a la página principal')}</span>
            </a>
          </div>
          <div className="rounded-3xl border border-border bg-card/80 p-6 shadow-card backdrop-blur-xl sm:p-8">
            <div className="mb-7">
              <p className="page-eyebrow">SUSCRI.net</p>
              <h2 className="text-2xl font-bold tracking-[-0.03em] text-foreground sm:text-3xl">{title}</h2>
              <div className="mt-2 text-sm leading-6 text-muted-foreground">{subtitle}</div>
            </div>
            {children}
          </div>
          {footer && <div className="mt-5 text-center text-sm text-muted-foreground">{footer}</div>}
        </div>
      </section>
    </main>
  );
}

function Feature({ icon: Icon, label }: { icon: typeof BarChart3; label: string }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-3.5">
      <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
      <p className="mt-3 text-xs font-semibold text-foreground">{label}</p>
    </div>
  );
}
